/**
 * CR309: Watermark Editor Component
 * 
 * Editor for watermark settings in the Components Studio.
 * Supports linked (same asset for front/back) or unlinked (different assets) modes.
 */

import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Droplets, Link, Unlink, Image, X } from "lucide-react";
import { getAssetImageSrc } from "@/lib/assets/getAppAssetPublicUrl";
import { useAssetsByType } from "@/hooks/useAssetsByType";
import { AssetPickerModal } from "./AssetPickerModal";
import type { WatermarkConfig, WatermarkPlacement, WatermarkScaleMode, ComponentsConfig } from "./DealCardsTab";
import type { AppAsset } from "@/lib/asset-studio/types";

// ================================================================
// CONSTANTS
// ================================================================

const DEFAULT_WATERMARK_CONFIG: WatermarkConfig = {
  enabled: false,
  link_faces: true,
  front_asset_id: null,
  back_asset_id: null,
  opacity: 0.08,
  placement: "center",
  scale_mode: "fit",
};

const PLACEMENT_OPTIONS: { value: WatermarkPlacement; label: string }[] = [
  { value: "center", label: "Center" },
  { value: "top-left", label: "Top Left" },
  { value: "top-right", label: "Top Right" },
  { value: "bottom-left", label: "Bottom Left" },
  { value: "bottom-right", label: "Bottom Right" },
];

const SCALE_MODE_OPTIONS: { value: WatermarkScaleMode; label: string }[] = [
  { value: "fit", label: "Fit" },
  { value: "fill", label: "Fill" },
];

// ================================================================
// ASSET SELECTOR SUB-COMPONENT
// ================================================================

interface AssetSelectorProps {
  label: string;
  assetId: string | null;
  assets: AppAsset[];
  loading: boolean;
  onSelect: (asset: { id: string; name: string } | null) => void;
  disabled?: boolean;
}

function AssetSelector({
  label,
  assetId,
  assets,
  loading,
  onSelect,
  disabled = false,
}: AssetSelectorProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  
  const selectedAsset = assetId ? assets.find(a => a.id === assetId) : null;
  
  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      
      <div className={`flex items-center gap-3 p-2.5 rounded-lg border bg-card ${disabled ? "opacity-50" : ""}`}>
        {/* Thumbnail */}
        {selectedAsset ? (
          <img
            src={getAssetImageSrc(selectedAsset) || ""}
            alt={selectedAsset.name}
            className="w-8 h-8 flex-shrink-0 object-contain opacity-30"
          />
        ) : (
          <div className="w-8 h-8 flex-shrink-0 bg-muted rounded flex items-center justify-center">
            <Droplets className="h-4 w-4 text-muted-foreground" />
          </div>
        )}
        
        {/* Name */}
        <div className="flex-1 min-w-0">
          <span className="text-sm truncate block">
            {selectedAsset ? selectedAsset.name : "None selected"}
          </span>
        </div>
        
        {/* Actions */}
        <div className="flex gap-1 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPickerOpen(true)}
            disabled={disabled}
            className="h-7 text-xs px-2"
          >
            {selectedAsset ? "Change" : "Select"}
          </Button>
          {selectedAsset && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSelect(null)}
              disabled={disabled}
              className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      
      {/* Picker Modal */}
      <AssetPickerModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        filterType="watermark"
        assets={assets}
        selectedId={assetId}
        onSelect={onSelect}
        title={`Select ${label}`}
        allowClear={true}
        loading={loading}
      />
    </div>
  );
}

// ================================================================
// MAIN COMPONENT
// ================================================================

interface WatermarkEditorProps {
  components: ComponentsConfig;
  onChange: (update: Partial<ComponentsConfig>) => void;
}

export function WatermarkEditor({ components, onChange }: WatermarkEditorProps) {
  const watermark = { ...DEFAULT_WATERMARK_CONFIG, ...components.watermark };
  
  // Load watermark assets
  const { assets, loading, hasAssets } = useAssetsByType({ type: "watermark" });
  
  // Update helper
  const updateWatermark = (updates: Partial<WatermarkConfig>) => {
    onChange({
      watermark: { ...watermark, ...updates },
    });
  };
  
  // Handle link toggle
  const handleLinkToggle = (linked: boolean) => {
    if (linked) {
      // When linking, copy front to back
      updateWatermark({
        link_faces: true,
        back_asset_id: watermark.front_asset_id,
      });
    } else {
      updateWatermark({ link_faces: false });
    }
  };
  
  // Handle front asset selection
  const handleFrontSelect = (asset: { id: string; name: string } | null) => {
    const newFrontId = asset?.id ?? null;
    if (watermark.link_faces) {
      // When linked, update both
      updateWatermark({
        front_asset_id: newFrontId,
        back_asset_id: newFrontId,
      });
    } else {
      updateWatermark({ front_asset_id: newFrontId });
    }
  };
  
  // Handle back asset selection
  const handleBackSelect = (asset: { id: string; name: string } | null) => {
    updateWatermark({ back_asset_id: asset?.id ?? null });
  };
  
  return (
    <div className="space-y-4">
      {/* Enabled Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Droplets className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm font-medium">Watermark</Label>
        </div>
        <Switch
          checked={watermark.enabled}
          onCheckedChange={(enabled) => updateWatermark({ enabled })}
        />
      </div>
      
      {!watermark.enabled && (
        <p className="text-xs text-muted-foreground">
          Enable watermark to add a subtle background graphic to card faces.
        </p>
      )}
      
      {watermark.enabled && (
        <>
          <Separator />
          
          {/* Link Front/Back Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {watermark.link_faces ? (
                <Link className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Unlink className="h-4 w-4 text-muted-foreground" />
              )}
              <Label className="text-xs">Link front & back</Label>
            </div>
            <Switch
              checked={watermark.link_faces}
              onCheckedChange={handleLinkToggle}
            />
          </div>
          
          {/* Asset Selection */}
          <div className="space-y-3">
            {!hasAssets && !loading && (
              <div className="p-3 rounded-lg bg-muted/30 border border-dashed text-center">
                <Image className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">
                  No watermark assets found. Add one in Asset Studio → Type: Watermark.
                </p>
              </div>
            )}
            
            {(hasAssets || loading) && (
              <>
                {watermark.link_faces ? (
                  <AssetSelector
                    label="Watermark (Both)"
                    assetId={watermark.front_asset_id ?? null}
                    assets={assets}
                    loading={loading}
                    onSelect={handleFrontSelect}
                  />
                ) : (
                  <>
                    <AssetSelector
                      label="Watermark (Front)"
                      assetId={watermark.front_asset_id ?? null}
                      assets={assets}
                      loading={loading}
                      onSelect={handleFrontSelect}
                    />
                    <AssetSelector
                      label="Watermark (Back)"
                      assetId={watermark.back_asset_id ?? null}
                      assets={assets}
                      loading={loading}
                      onSelect={handleBackSelect}
                    />
                  </>
                )}
              </>
            )}
          </div>
          
          <Separator />
          
          {/* Presentation Controls */}
          <div className="space-y-4">
            {/* Opacity */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Opacity</Label>
                <Badge variant="outline" className="text-[10px]">
                  {Math.round((watermark.opacity ?? 0.08) * 100)}%
                </Badge>
              </div>
              <Slider
                value={[(watermark.opacity ?? 0.08) * 100]}
                onValueChange={([val]) => updateWatermark({ opacity: val / 100 })}
                min={0}
                max={20}
                step={1}
                className="w-full"
              />
              <p className="text-[10px] text-muted-foreground">
                Recommended: 5-10% for subtle background effect
              </p>
            </div>
            
            {/* Placement */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Placement</Label>
              <Select
                value={watermark.placement ?? "center"}
                onValueChange={(val) => updateWatermark({ placement: val as WatermarkPlacement })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLACEMENT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Scale Mode */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Scale Mode</Label>
              <Select
                value={watermark.scale_mode ?? "fit"}
                onValueChange={(val) => updateWatermark({ scale_mode: val as WatermarkScaleMode })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCALE_MODE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
